import * as React from 'react'
import map from 'lodash/map'
import {exhaustive} from '@freckle/exhaustive'
import {mmap} from '@freckle/maybe'

import {createSelectorOptionAsItem} from '@frontrow/console/src/components/generic-selector'
import {
  type RlStandardSetT,
  filterStandardSetsForDarkTest,
  fetchRlStandardSets,
  appendIdToStandardSetDescription
} from '@frontrow/console/src/entities/models/rl-standard-set'
import {ContentAreas} from '@frontrow/console/src/entities/helpers/content-area'

import {
  SelfContainedTeacherSelector,
  type FetchedOrBeFetchedT
} from '@frontrow/console/src/components/self-contained-teacher-selector'
import {patchTeacherById} from '@frontrow/console/src/helpers/api-teachers'

type ModeT =
  | {
      tag: 'dark-test-based-passed-systems-on-teacher-email'
      mathStandardSets: Array<RlStandardSetT>
      teacherEmail: string
    }
  | {
      tag: 'retrieve-all-without-dark-testing-and-perform-update'
      onError: (where: 'retrieving' | 'updating', a: unknown) => void
      teacherId: number
    }

type ComponentLabelsT = {
  formLabel?: string | null
  loadingText: string
  yourSchoolSettingText?: string
}

type Props = {
  id: string
  mode: ModeT
  schoolMathStandardSet?: RlStandardSetT | null
  selectedMathStandardSetId: string
  onChangeSelection: (a: string) => void
  labels: ComponentLabelsT
}

export function TeacherMathSystemSelector({
  id,
  mode,
  selectedMathStandardSetId: selectedItem,
  labels,
  schoolMathStandardSet,
  onChangeSelection
}: Props): React.ReactElement {
  const yourSchoolSettingText = labels.yourSchoolSettingText

  const itemToSelectorOption = (mathStandardSet: RlStandardSetT) => {
    const mathStandardSetLabel =
      mmap(s => s.id, schoolMathStandardSet) === mathStandardSet.id &&
      yourSchoolSettingText !== null &&
      yourSchoolSettingText !== undefined
        ? `${mathStandardSet.description} (${yourSchoolSettingText})`
        : mathStandardSet.description
    return createSelectorOptionAsItem(
      mathStandardSet.id,
      mathStandardSetLabel,
      false,
      mathStandardSet.id
    )
  }

  const itemsToCollection = (mathStandardSets: RlStandardSetT[]) =>
    map(mathStandardSets, itemToSelectorOption)

  const onChangeSelectionP = (selectedSetting: string) => {
    switch (mode.tag) {
      case 'dark-test-based-passed-systems-on-teacher-email':
        return Promise.resolve(onChangeSelection(selectedSetting))
      case 'retrieve-all-without-dark-testing-and-perform-update':
        return setTeacherStandardSet(mode.teacherId, selectedSetting)
          .then(() => onChangeSelection(selectedSetting))
          .catch((err: unknown) => mode.onError('updating', err))
      default:
        return exhaustive(mode as never, 'ModeT')
    }
  }

  const {yourSchoolSettingText: _yourSchoolSettingText, ...subLabels} = labels

  const subProps = {
    id,
    labels: subLabels,
    fetchItems: getSystemsByMode(mode, selectedItem),
    selectedItem,
    itemsToCollection,
    onChangeSelection: onChangeSelectionP,
    onFetchError:
      mode.tag === 'retrieve-all-without-dark-testing-and-perform-update'
        ? (err: unknown) => mode.onError('retrieving', err)
        : null
  }

  return <SelfContainedTeacherSelector {...subProps} />
}

const getSystemsByMode = (
  mode: ModeT,
  selectedStandardSetId: string
): FetchedOrBeFetchedT<Array<RlStandardSetT>> => {
  switch (mode.tag) {
    case 'dark-test-based-passed-systems-on-teacher-email':
      return {
        tag: 'fetched',
        value: filterStandardSetsForDarkTest(
          mode.mathStandardSets,
          mode.teacherEmail,
          selectedStandardSetId
        )
      }
    case 'retrieve-all-without-dark-testing-and-perform-update':
      return {
        tag: 'be-fetched',
        fetch: async () => {
          const standardSets = (await fetchRlStandardSets(ContentAreas.math)) as RlStandardSetT[]
          return appendIdToStandardSetDescription(standardSets)
        }
      }
    default:
      return exhaustive(mode as never, 'ModeT')
  }
}

const setTeacherStandardSet = (teacherId: number, mathStandardSetId: string): Promise<unknown> =>
  patchTeacherById(teacherId, {mathStandardSetId})
